import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, ChevronDown, UserPlus, Trash2, Zap, X, RotateCcw } from 'lucide-react';
import { api } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { campaignStatusMeta, enrollmentStatusMeta, CAMPAIGN_STATUSES, fmtPercent } from '../../lib/outreach';
import EmptyState from '../../components/crm/EmptyState';
import StepEditor from '../../components/crm/StepEditor';
import CampaignTargetingPanel from '../../components/admin/CampaignTargetingPanel';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import BackLink from '../../components/admin/BackLink';

export default function OutreachDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [steps, setSteps] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusOpen, setStatusOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmRemoveEnrollment, setConfirmRemoveEnrollment] = useState(null);
  const [confirmRunNow, setConfirmRunNow] = useState(false);
  const [confirmPromote, setConfirmPromote] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [confirmRestart, setConfirmRestart] = useState(false);
  const [restarting, setRestarting] = useState(false);
  // "Run now" — manual trigger of the per-campaign cron logic. Works
  // regardless of whether auto_enroll is on, so an admin can always
  // force a dispatch instead of waiting for the scheduled run.
  const [running, setRunning] = useState(false);
  const [runResult, setRunResult] = useState(null);

  const runNow = async () => {
    setRunning(true);
    setRunResult(null);
    try {
      const res = await api.post(`/api/admin/outreach/campaigns/${id}/run-now`, {});
      setRunResult(res);
      await load();
    } catch (err) {
      setRunResult({ error: err?.data?.error || err?.message || 'Run failed' });
    } finally {
      setRunning(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [detail, enrol, st] = await Promise.all([
        api.get(`/api/admin/outreach/campaigns/${id}`),
        api.get(`/api/admin/outreach/campaigns/${id}/enrollments?limit=100`),
        api.get(`/api/admin/outreach/campaigns/${id}/stats`),
      ]);
      setCampaign(detail.campaign);
      setSteps(detail.steps || []);
      setEnrollments(enrol.enrollments || []);
      setStats(st);
    } catch (err) {
      setError(err?.message || 'Failed to load campaign');
    } finally {
      setLoading(false);
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const changeStatus = async (target) => {
    setStatusOpen(false);
    try {
      await api.put(`/api/admin/outreach/campaigns/${id}`, { status: target });
      await load();
    } catch (err) { alert(err?.message || 'Failed'); }
  };

  const promoteFromDryRun = async () => {
    setPromoting(true);
    try {
      await api.patch(`/api/admin/outreach/campaigns/${id}`, { dryRun: false });
      await load();
    } catch (err) {
      alert(err?.data?.error || err?.message || 'Failed to promote out of dry-run');
    } finally {
      setPromoting(false);
    }
  };

  // Re-enable dry-run on an active campaign. Used when the operator wants
  // to test a content change on a live campaign without immediately firing
  // real sends. Non-destructive — just a flag flip — so no confirm needed.
  const enableDryRun = async () => {
    setPromoting(true);
    try {
      await api.patch(`/api/admin/outreach/campaigns/${id}`, { dryRun: true });
      await load();
    } catch (err) {
      alert(err?.data?.error || err?.message || 'Failed to enable dry-run');
    } finally {
      setPromoting(false);
    }
  };

  const restartCampaign = async () => {
    setRestarting(true);
    try {
      await api.post(`/api/admin/outreach/campaigns/${id}/restart`, {});
      await load();
    } catch (err) {
      alert(err?.data?.error || err?.message || 'Failed to restart campaign');
    } finally {
      setRestarting(false);
    }
  };

  const performRemoveEnrollment = async () => {
    const target = confirmRemoveEnrollment;
    if (!target) return;
    try {
      await api.delete(`/api/admin/outreach/enrollments/${target.id}`);
      // Reload everything so the stat panel ("Enrolled", "Sends" etc.)
      // reflects the new state instead of going stale until the next
      // navigation. Cheap call, runs in parallel.
      await load();
    } catch (err) {
      alert(err?.data?.error || err?.message || 'Failed to remove enrollment');
    }
  };

  const deleteCampaign = async () => {
    setDeleting(true);
    try {
      await api.delete(`/api/admin/outreach/campaigns/${id}`);
      navigate('/admin/communications?tab=sequences');
    } catch (err) {
      alert(err?.data?.error || err?.message || 'Failed to delete campaign');
      setDeleting(false);
    }
  };

  if (user?.role !== 'nexsav_admin' && user?.role !== 'ops') {
    return <EmptyState title="Staff only" />;
  }

  return (
    <div className="max-w-5xl mx-auto">
      <BackLink to="/admin/communications?tab=sequences" className="inline-flex items-center gap-1.5 text-[12px] text-gray-500 hover:text-white mb-4">
        <ArrowLeft className="w-3 h-3" /> Back
      </BackLink>
      {loading && !campaign && <div className="animate-pulse h-20 rounded-xl bg-white/[0.03]" />}
      {error && <div className="text-[12px] text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg p-3">{error}</div>}
      {campaign && (
        <>
          <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
            <div>
              <h1 className="text-xl md:text-2xl font-semibold text-white tracking-tight">{campaign.name}</h1>
              {campaign.description && <div className="text-[13px] text-gray-500 mt-1">{campaign.description}</div>}
            </div>
            <div className="flex items-center gap-2">
              {campaign.dryRun && (
                <span
                  data-testid="campaign-dry-run-badge"
                  title="Campaign is in dry-run. No real emails will be sent. Promote out of dry-run to start sending."
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] uppercase tracking-wider border border-amber-500/40 bg-amber-500/10 text-amber-200"
                >
                  Dry run
                </span>
              )}
              {/* Run-now visibility:
                    - Draft (always dry): "Run dry test" — preview before activation
                    - Active + dry_run=1 (manually re-enabled): "Run dry test"
                    - Active + dry_run=0: "Run now" (real send)
                    - Paused / archived: hidden (sending is paused/closed) */}
              {(campaign.status === 'active' || campaign.status === 'draft') && (
                <button
                  onClick={() => setConfirmRunNow(true)}
                  disabled={running}
                  data-testid="campaign-run-now"
                  title={
                    campaign.dryRun
                      ? 'Render a dry-run preview: no enrollments written, no emails sent'
                      : (campaign.autoEnroll
                        ? 'Run the auto-enroll + send batch immediately for this campaign'
                        : 'Process due sends for already-enrolled accounts immediately')
                  }
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] disabled:opacity-50 ${
                    campaign.dryRun
                      ? 'bg-amber-500/[0.12] border border-amber-500/30 text-amber-200 hover:bg-amber-500/20'
                      : 'bg-[#B0FC38]/[0.12] border border-[#B0FC38]/30 text-[#D7FF8A] hover:bg-[#B0FC38]/20'
                  }`}
                >
                  <Zap className="w-3 h-3" /> {running ? 'Running…' : (campaign.dryRun ? 'Run dry test' : 'Run now')}
                </button>
              )}
              {/* Dry-run toggle. On draft, dry_run is forced on and gets
                  cleared automatically when the campaign is activated, so
                  neither button shows in draft. On active, the operator
                  can flip dry-run on/off freely (e.g. to test a content
                  edit before it goes out for real). */}
              {campaign.status === 'active' && campaign.dryRun && (
                <button
                  onClick={() => setConfirmPromote(true)}
                  disabled={promoting}
                  data-testid="campaign-promote"
                  title="Disable dry-run. Real sends will resume on the next Run-now or scheduled cron."
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] bg-[#B0FC38]/[0.12] border border-[#B0FC38]/30 text-[#B0FC38] hover:bg-[#B0FC38]/20 disabled:opacity-50"
                >
                  {promoting ? 'Disabling…' : 'Disable dry-run'}
                </button>
              )}
              {campaign.status === 'active' && !campaign.dryRun && user?.role === 'nexsav_admin' && (
                <button
                  onClick={enableDryRun}
                  disabled={promoting}
                  data-testid="campaign-enable-dry-run"
                  title="Switch this campaign back into dry-run for testing. Real sends pause until you disable it."
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] text-amber-300/80 hover:text-amber-200 hover:bg-amber-500/[0.06] disabled:opacity-50"
                >
                  {promoting ? 'Enabling…' : 'Test in dry-run'}
                </button>
              )}
              {/* Restart action — visible when at least one enrollment has
                  finished the drip. Resets every non-unsubscribed enrollment
                  to step 0 / next_send_at=now so a follow-up Run-now re-fires
                  the whole sequence. Designed for recurring use cases like
                  "first of the month" reminders. */}
              {campaign.status === 'active' && (stats?.enrollments?.completed ?? 0) > 0 && (
                <button
                  onClick={() => setConfirmRestart(true)}
                  disabled={restarting}
                  data-testid="campaign-restart"
                  title={`Reset all ${stats?.enrollments?.completed ?? 0} completed enrollment${(stats?.enrollments?.completed ?? 0) === 1 ? '' : 's'} back to step 0 so the campaign can run again. Used for recurring campaigns.`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] bg-[#B0FC38]/[0.12] border border-[#B0FC38]/30 text-[#B0FC38] hover:bg-[#B0FC38]/20 disabled:opacity-50"
                >
                  <RotateCcw className="w-3 h-3" /> {restarting ? 'Restarting…' : 'Restart campaign'}
                </button>
              )}
              <div className="relative">
              <button onClick={() => setStatusOpen((v) => !v)} className="inline-flex items-center gap-2 px-2 py-1 rounded-lg hover:bg-white/[0.03]">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider border ${campaignStatusMeta(campaign.status).tone}`}>
                  {campaignStatusMeta(campaign.status).label}
                </span>
                <ChevronDown className="w-3.5 h-3.5 text-gray-500" />
              </button>
              {statusOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setStatusOpen(false)} />
                  <div className="absolute right-0 top-full mt-1 z-50 min-w-[180px] rounded-xl border border-white/[0.08] bg-[#0a0a0f] p-1.5">
                    {CAMPAIGN_STATUSES.filter((s) => s !== campaign.status).map((s) => (
                      <button key={s} onClick={() => changeStatus(s)} className="w-full text-left px-2 py-1.5 rounded-lg text-[12px] text-gray-300 hover:bg-white/[0.04] hover:text-white">
                        {campaignStatusMeta(s).label}
                      </button>
                    ))}
                  </div>
                </>
              )}
              </div>
            </div>
          </div>

          {runResult && (
            <div data-testid="run-now-result" className={`mb-4 rounded-lg border p-3 text-[12px] ${
              runResult.error
                ? 'border-red-500/25 bg-red-500/10 text-red-300'
                : runResult.dryRun
                  ? 'border-amber-500/25 bg-amber-500/[0.06] text-amber-200'
                  : 'border-[#B0FC38]/25 bg-[#B0FC38]/[0.06] text-[#D7FF8A]'
            }`}>
              {runResult.error
                ? `Run failed: ${runResult.error}`
                : runResult.dryRun
                  ? <DryRunResult result={runResult} />
                  : `Enrolled ${runResult.enrolled || 0} new account${runResult.enrolled === 1 ? '' : 's'} · sent ${runResult.sent || 0} email${runResult.sent === 1 ? '' : 's'}${runResult.skipped ? ` · skipped ${runResult.skipped}` : ''}.`}
            </div>
          )}

          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
              <Stat label="Enrolled" value={String(stats.enrolledCount)} />
              <Stat label="Sends" value={String(stats.sendsCount)} />
              <Stat
                label="Failed"
                value={String(stats.failedCount ?? 0)}
                tone={(stats.failedCount ?? 0) > 0 ? 'alert' : null}
              />
              <Stat label="Bounce rate" value={fmtPercent(stats.bounceRate)} tone={stats.bounceRate > 0.05 ? 'alert' : null} />
              <Stat label="Unsubscribe rate" value={fmtPercent(stats.unsubscribeRate)} tone={stats.unsubscribeRate > 0.1 ? 'alert' : null} />
            </div>
          )}

          <div className="mb-6">
            <CampaignTargetingPanel campaign={campaign} />
          </div>

          <div className="rounded-2xl border border-white/[0.05] bg-white/[0.015] p-5 mb-6">
            <h3 className="text-[13px] font-semibold text-white mb-3">Steps ({steps.length})</h3>
            <StepEditor campaignId={id} steps={steps} onChange={setSteps} />
          </div>

          <div className="rounded-2xl border border-white/[0.05] bg-white/[0.015] p-5 mb-6">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <h3 className="text-[13px] font-semibold text-white">Enrollments ({enrollments.length})</h3>
                <span
                  data-testid="auto-enroll-indicator"
                  title={campaign.autoEnroll
                    ? 'When this campaign is active, the cron auto-enrolls new matching accounts.'
                    : 'New matching accounts will not be auto-enrolled. Use the Enroll page to turn on.'}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider border ${
                    campaign.autoEnroll
                      ? 'bg-[#B0FC38]/[0.12] border-[#B0FC38]/40 text-[#B0FC38]'
                      : 'bg-white/[0.03] border-white/[0.08] text-gray-500'
                  }`}>
                  <Zap className="w-2.5 h-2.5" /> Auto-enroll {campaign.autoEnroll ? 'on' : 'off'}
                </span>
              </div>
              <button onClick={() => navigate(`/admin/outreach/${id}/enroll`)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] bg-[#B0FC38] hover:bg-[#9BE22E] text-gray-900">
                <UserPlus className="w-3 h-3" /> Manage enrollments
              </button>
            </div>
            {enrollments.length === 0 ? (
              <EmptyState title="No enrollments yet" description="Use the Manage enrollments button to add accounts or turn on auto-enroll." />
            ) : (
              <div className="space-y-1">
                {enrollments.map((e) => {
                  const acc = e.account || {};
                  const accountName = acc.name || (e.accountId ? `${e.accountId.slice(0, 8)}…` : 'Unknown');
                  const meta = enrollmentStatusMeta(e.status);
                  return (
                    <div key={e.id} data-testid={`enrollment-row-${e.id}`} className="p-2 rounded-lg border border-white/[0.04] bg-white/[0.015] flex items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 text-[13px]">
                          {acc.slug ? (
                            <Link to={`/admin/accounts/${acc.slug}`} className="text-white hover:underline truncate">{accountName}</Link>
                          ) : (
                            <span className="text-white truncate">{accountName}</span>
                          )}
                          {acc.dbn && <span className="text-[10px] text-gray-500">{acc.dbn}</span>}
                        </div>
                        <div className="text-[10.5px] text-gray-500 mt-0.5">
                          Step {e.currentStep}
                          {acc.borough && ` · ${acc.borough}`}
                          {acc.district && ` · D${acc.district}`}
                          {e.lastSentAt && ` · last sent ${new Date(e.lastSentAt).toLocaleDateString()}`}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider border ${meta.tone}`}>
                          {meta.label}
                        </span>
                        <button
                          type="button"
                          onClick={() => setConfirmRemoveEnrollment({ id: e.id, name: accountName })}
                          title="Remove from campaign"
                          data-testid={`enrollment-remove-${e.id}`}
                          className="p-1 rounded text-gray-500 hover:text-red-400 hover:bg-red-500/10"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {user?.role === 'nexsav_admin' && (
            <div className="rounded-2xl border border-red-500/15 bg-red-500/[0.03] p-5">
              <h3 className="text-[13px] font-semibold text-white mb-2">Danger zone</h3>
              <p className="text-[11px] text-gray-500 mb-3">
                Deleting a campaign cascades to its steps + enrollments + sends.
                This cannot be undone.
              </p>
              <button
                type="button"
                data-testid="campaign-delete-button"
                onClick={() => setConfirmDelete(true)}
                disabled={deleting}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-[12px] text-red-300 bg-red-500/10 border border-red-500/20 hover:bg-red-500/20 disabled:opacity-50"
              >
                <Trash2 className="w-3 h-3" /> {deleting ? 'Deleting…' : 'Delete campaign'}
              </button>
            </div>
          )}

          <ConfirmDialog
            open={confirmDelete}
            onClose={() => setConfirmDelete(false)}
            onConfirm={deleteCampaign}
            title="Delete campaign?"
            description={`This permanently removes "${campaign.name}", all of its steps, enrollments, and send history. This cannot be undone.`}
            confirmLabel="Delete campaign"
            destructive
          />
          <ConfirmDialog
            open={!!confirmRemoveEnrollment}
            onClose={() => setConfirmRemoveEnrollment(null)}
            onConfirm={performRemoveEnrollment}
            title="Remove from campaign?"
            description={
              confirmRemoveEnrollment
                ? `${confirmRemoveEnrollment.name} will be removed from "${campaign.name}". They won't receive any further emails from this campaign. Send history is preserved.`
                : ''
            }
            confirmLabel="Remove"
            destructive
          />
          <ConfirmDialog
            open={confirmRunNow}
            onClose={() => setConfirmRunNow(false)}
            onConfirm={runNow}
            title={campaign.dryRun ? 'Render dry-run preview?' : 'Run enrollment + send now?'}
            description={
              campaign.dryRun
                ? `This is a DRY-RUN preview for "${campaign.name}". No enrollments will be written, no emails will be dispatched. The response will show the rendered email body for each matching account so you can verify token substitution and content. Promote out of dry-run when you're ready to send for real.`
                : `Running now triggers the same logic the daily cron uses for "${campaign.name}":\n\n` +
                  `• Enroll any account that matches Targeting and isn't already enrolled\n` +
                  `• Send the next-due step email to up to 200 active enrollments (currently ${stats?.enrollments?.active ?? '—'} active)\n\n` +
                  `This will dispatch real emails through Resend. There is no undo. Make sure you've previewed the email content and reviewed the eligible-accounts list before continuing.`
            }
            confirmLabel={campaign.dryRun ? 'Render preview' : 'Run now'}
            destructive={!campaign.dryRun}
          />
          <ConfirmDialog
            open={confirmRestart}
            onClose={() => setConfirmRestart(false)}
            onConfirm={restartCampaign}
            title="Restart campaign?"
            description={
              `Resets every non-unsubscribed enrollment in "${campaign.name}" back to step 0 with next_send_at=now. The next Run-now (or scheduled cron) will re-fire the whole drip sequence to the same recipients.\n\n` +
              `Use this for recurring campaigns (e.g. monthly check-ins). Unsubscribed accounts are NOT touched. Opt-out is permanent. Send history is preserved for analytics.\n\n` +
              `Currently ${stats?.enrollments?.completed ?? 0} completed, ${stats?.enrollments?.active ?? 0} active, ${stats?.enrollments?.unsubscribed ?? 0} unsubscribed.`
            }
            confirmLabel="Restart"
          />
          <ConfirmDialog
            open={confirmPromote}
            onClose={() => setConfirmPromote(false)}
            onConfirm={promoteFromDryRun}
            title="Promote out of dry-run?"
            description={
              `Once promoted, the next time the cron runs (or you click Run now), real emails will be dispatched through Resend for "${campaign.name}". Make sure you've reviewed the dry-run previews and confirmed the targeting, sender identity, and email content are correct.\n\n` +
              `You can switch back to dry-run later by patching the campaign, but any sends that have already happened cannot be unsent.`
            }
            confirmLabel="Promote · enable real sends"
            destructive
          />
        </>
      )}
    </div>
  );
}

// TipTap emits empty <p></p> for blank lines and Tailwind preflight zeroes
// out <p> margins, so without normalization + scoped CSS the dry-run pane
// renders every paragraph crammed against the next. Mirrors the editor's
// TemplatePreviewPane so what the operator sees here matches both the
// in-editor preview AND what Gmail/Outlook will display (sendEmail runs
// the same normalize step in _shared/email-html.js).
function normalizeEmptyParagraphs(html) {
  return String(html ?? '').replace(/<p>(\s|&nbsp;)*<\/p>/gi, '<p><br/></p>');
}

function DryRunResult({ result }) {
  const [openIdx, setOpenIdx] = useState(null);
  const previews = result.previews || [];
  return (
    <div className="space-y-2">
      <div className="font-medium">
        Dry-run complete · would enroll {result.wouldEnroll ?? 0} new account{result.wouldEnroll === 1 ? '' : 's'}
        {' · '}would send to {result.wouldSendToDue ?? 0} due enrollment{result.wouldSendToDue === 1 ? '' : 's'}
      </div>
      <div className="text-[11px] text-amber-200/80">{result.note}</div>
      {previews.length > 0 && (
        <div className="mt-2 space-y-1.5">
          <style>{`
            .dry-run-body p { margin: 0 0 0.75em 0; }
            .dry-run-body ul { list-style: disc; padding-left: 1.5em; margin: 0.5em 0; }
            .dry-run-body ol { list-style: decimal; padding-left: 1.5em; margin: 0.5em 0; }
            .dry-run-body li { margin: 0.25em 0; }
            .dry-run-body li > p { margin: 0; }
            .dry-run-body strong { font-weight: 600; }
            .dry-run-body em { font-style: italic; }
            .dry-run-body u { text-decoration: underline; }
            .dry-run-body s { text-decoration: line-through; }
            .dry-run-body a { color: #2563eb; text-decoration: underline; }
            .dry-run-body h1 { font-size: 1.5em; font-weight: 600; margin: 0.75em 0 0.5em; }
            .dry-run-body h2 { font-size: 1.25em; font-weight: 600; margin: 0.75em 0 0.5em; }
            .dry-run-body h3 { font-size: 1.1em; font-weight: 600; margin: 0.75em 0 0.5em; }
            .dry-run-body hr { border: 0; border-top: 1px solid #e5e7eb; margin: 1em 0; }
            .dry-run-body img { max-width: 100%; height: auto; border-radius: 4px; margin: 0.5em 0; }
          `}</style>
          <div className="text-[10px] uppercase tracking-wider text-amber-200/70">Rendered step-0 preview ({previews.length})</div>
          {previews.map((p, i) => (
            <div key={p.accountId} className="rounded-md border border-amber-500/20 bg-amber-500/[0.04]">
              <button
                type="button"
                onClick={() => setOpenIdx(openIdx === i ? null : i)}
                className="w-full text-left px-3 py-2 flex items-center justify-between hover:bg-amber-500/[0.06]"
              >
                <div className="flex flex-col items-start min-w-0">
                  <span className="text-[12px] text-white truncate">{p.accountName}</span>
                  <span className="text-[10px] text-amber-200/60 truncate">{p.contactName} · {p.contactEmail}</span>
                </div>
                <span className="text-[10px] text-amber-200/60">{openIdx === i ? '−' : '+'}</span>
              </button>
              {openIdx === i && (
                <div className="px-3 pb-3 space-y-2">
                  <div className="text-[10px] text-amber-200/60 uppercase tracking-wider">Subject</div>
                  <div className="text-[12px] text-white font-mono">{p.subject}</div>
                  <div className="text-[10px] text-amber-200/60 uppercase tracking-wider">Body (HTML)</div>
                  <div
                    className="dry-run-body bg-white text-black rounded p-3 text-[12px] leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: normalizeEmptyParagraphs(p.html) }}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, tone = null }) {
  const toneCls = tone === 'alert' ? 'border-red-500/20 bg-red-500/5' : 'border-white/[0.05] bg-white/[0.015]';
  return (
    <div className={`rounded-xl border p-4 ${toneCls}`}>
      <div className="text-[10px] text-gray-500 uppercase tracking-wider">{label}</div>
      <div className="text-[20px] text-white font-semibold mt-0.5">{value}</div>
    </div>
  );
}
